import * as Highcharts from 'highcharts';
import { SeriesElementCustomData } from '../types/chartData';
import { BaseChartProps } from '../types/chartProps';
import { formatValue } from './formatters';
import { supportsMultipleYAxis } from './axisUtils';
import { ValueType } from '../types';
import { escapeHtml } from '@opengov/reporting-types/src/escapeHtml';

export function dataLine(label: string, value: string | number | null | undefined) {
  const val = value ?? '-';
  return `<div style="font-size: 14px"><b>${escapeHtml(label)}</b> <span style="color: #616365;">${escapeHtml(
    val
  )}</span></div>`;
}

export function header(source: string | undefined, category: string, value: string) {
  return (
    `<div style="color: #616365; font-size: 14px;">${escapeHtml(source)}</div>` +
    `<div style="font-size: 20px;"><b>${escapeHtml(category)}</b></div>` +
    `<div style="color: #616365; font-size: 20px;">${escapeHtml(value)}</div>`
  );
}

export function createTooltipFormatterForStackingNormal(props: BaseChartProps) {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const category = this.series.options?.custom?.displayName ?? this.series.name;
    const valueDisplayType = this.series.options?.custom?.valueDisplayType as ValueType;
    const isPrimaryYAxis = this.series.options?.custom?.isPrimaryYAxis;
    const displayValue = formatValue(props.formatting, this.y ?? 0, valueDisplayType);

    if (isPrimaryYAxis) {
      return (
        header(this.key, category, displayValue) +
        dataLine('Total', formatValue(props.formatting, this.total ?? 0, valueDisplayType))
      );
    }

    return header(this.key, category, displayValue);
  };
}

export function createTooltipFormatterForStackingPercent(props: BaseChartProps) {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const category = this.series.options?.custom?.displayName ?? this.series.name;
    const valueDisplayType = this.series.options?.custom?.valueDisplayType as ValueType;
    const isPrimaryYAxis = this.series.options?.custom?.isPrimaryYAxis;
    const displayValue = formatValue(props.formatting, this.percentage / 100, 'percentage');

    if (isPrimaryYAxis) {
      return (
        header(this.key, category, displayValue) +
        dataLine('Amount', formatValue(props.formatting, this.y ?? 0, valueDisplayType)) +
        dataLine('Total', formatValue(props.formatting, this.total ?? 0, valueDisplayType))
      );
    }

    return header(this.key, category, displayValue);
  };
}

export function createDefaultTooltipFormatter(props: BaseChartProps) {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const custom = this.point?.options?.custom ?? {};
    const category = this.series.options?.custom?.displayName ?? this.series.name;
    const valueDisplayType = this.series.options?.custom?.valueDisplayType as ValueType;
    const isPrimaryYAxis = this.series.options?.custom?.isPrimaryYAxis;

    const displayValue = props.isPercentChart
      ? formatValue(props.formatting, this.y ?? 0, 'percentage')
      : formatValue(props.formatting, this.y ?? 0, valueDisplayType);

    const customObjWithKeysAndValues: Record<keyof SeriesElementCustomData, string> = {
      amount: 'Amount',
      total: 'Total',
      percentTotal: 'Percent of Total',
      targetStatus: 'Target Status',
      change: 'Change'
    };

    const customKeysArray: (keyof SeriesElementCustomData)[] = [
      'amount',
      'total',
      'targetStatus',
      'percentTotal',
      'change'
    ];

    let lowerTooltip = ``;

    for (const key of customKeysArray) {
      if (custom[key]) {
        let percentValue = '';
        let formattedValue = '';
        switch (key) {
          case 'percentTotal':
          case 'change':
            percentValue = formatValue(props.formatting, custom[key], 'percentage');
            lowerTooltip += dataLine(customObjWithKeysAndValues[key], percentValue);
            break;
          case 'targetStatus':
            lowerTooltip += dataLine(customObjWithKeysAndValues[key], custom[key]);
            break;
          default:
            formattedValue = formatValue(props.formatting, custom[key], valueDisplayType);
            lowerTooltip += dataLine(customObjWithKeysAndValues[key], formattedValue);
        }
      }
    }

    lowerTooltip = lowerTooltip ? `<br/>${lowerTooltip}` : '';

    if (isPrimaryYAxis) {
      return header(this.key, category, displayValue) + lowerTooltip;
    }

    return header(this.key, category, displayValue);
  };
}

export function createDefaultTooltipFormatterForMultipleYAxis(props: BaseChartProps) {
  const stackingNormalFormatter = createTooltipFormatterForStackingNormal(props);
  const stackingPercentFormatter = createTooltipFormatterForStackingPercent(props);
  const defaultFormatter = createDefaultTooltipFormatter(props);
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const stacking = this.series.options?.custom?.stacking;

    if (stacking === 'normal') {
      return stackingNormalFormatter.call(this);
    }

    if (stacking === 'percent') {
      return stackingPercentFormatter.call(this);
    }

    // Fallback to default formatter
    return defaultFormatter.call(this);
  };
}

function applyTooltipOptionsForSingleYAxis(options: Highcharts.Options, params: BaseChartProps) {
  let formatter = createDefaultTooltipFormatter(params);

  if (params.stacking === 'normal') {
    formatter = createTooltipFormatterForStackingNormal(params);
  } else if (params.stacking === 'percent') {
    formatter = createTooltipFormatterForStackingPercent(params);
  }

  options.tooltip = {
    ...options.tooltip,
    formatter
  };

  return options;
}

function applyTooltipOptionsForMultipleYAxis(options: Highcharts.Options, params: BaseChartProps) {
  options.tooltip = {
    ...options.tooltip,
    formatter: createDefaultTooltipFormatterForMultipleYAxis(params)
  };

  return options;
}

export function applyTooltipOptions(options: Highcharts.Options, params: BaseChartProps) {
  if (supportsMultipleYAxis(params.yAxis)) {
    return applyTooltipOptionsForMultipleYAxis(options, params);
  }

  return applyTooltipOptionsForSingleYAxis(options, params);
}
